import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {
  PROTEIN_MAP_Y_COMPONENTS,
  PROTEIN_MAP_Z_COMPONENTS,
  SliceComponentName,
} from 'constants/MapsConstants';
import {listenToAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import {INPUT_BUBBLE_OPERATION} from 'molbio/maps/InputBubble';
import PolymerLinearMapView from 'molbio/maps/linear/PolymerLinearMapView';
import PolymerSliceItemSource, {
  PolymerSliceItemSourceAttributes,
} from 'molbio/maps/linear/PolymerSliceItemSource';
import {ProteinLinearMapState} from 'molbio/maps/MapStates';
import ProteinMapOptions from 'molbio/maps/options/ProteinMapOptions';
import SimplifiedProteinMapOptions from 'molbio/maps/options/SimplifiedProteinMapOptions';
import {ShowInputBubbleOptions} from 'molbio/maps/SequenceMapUtil';
import Type from 'molbio/maps/Type';
import Protein from 'molbio/proteins/Protein';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinInputBubble from 'molbio/proteins/ProteinInputBubble';
import ProteinStore from 'molbio/proteins/ProteinStore';
import * as ProteinProperties from 'molbio/proteins/util/ProteinProperties';
import ProteinMapTabState from 'molbio/tabstates/ProteinMapTabState';
import notifications from 'notifications';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import {selectRange} from 'toolbar/protein/ProteinUIGlobalState';
import * as bclip from 'util/BenchlingClipboard';

class ProteinSliceItemSource extends PolymerSliceItemSource<
  Protein,
  ProteinLinearMapState,
  ProteinMapTabState
> {
  override initialize(
    options: PolymerSliceItemSourceAttributes<Protein, ProteinLinearMapState, ProteinMapTabState>
  ): void {
    super.initialize(options);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.get('mapstate').protein(), 'change', this.update);
  }

  sliceViewY(): Array<SliceComponentName> {
    return PROTEIN_MAP_Y_COMPONENTS;
  }
  sliceViewZ(): Array<SliceComponentName> {
    return PROTEIN_MAP_Z_COMPONENTS;
  }
}

export function validateAndStripAminoAcids(text: string): string | null {
  // @ts-expect-error: it's safe to call on a string
  const [aminoAcidChars, invalidChars] = _.partition(text, (char: string) =>
    ProteinProperties.areAminoAcidsValid(char)
  );

  const aminoAcids = aminoAcidChars.join('');
  if (aminoAcids.length === 0) {
    notifications.info('No valid amino acids were pasted.');
    return null;
  }

  if (
    invalidChars.length > 0 &&
    // Only notify if skipped character wasn't whitespace
    invalidChars.some((char) => !/\s/.test(char))
  ) {
    notifications.info('Some amino acids pasted were not valid and were removed.');
  }
  return aminoAcids;
}

class ProteinMap extends PolymerLinearMapView<Protein, ProteinLinearMapState, ProteinMapTabState> {
  $reactMount!: JQuery;
  currentOperation?: INPUT_BUBBLE_OPERATION;

  static initStatics(): void {
    this.prototype.id = 'protein-map';
    this.prototype.inputSource = 'protein-map-input';
  }

  override initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    this.$reactMount = $('<div>', {class: 'js-reactMount'});
    this.$el.append(this.$reactMount);
    addTestAttrsToDOMElement(this.$el[0], {component: 'ProteinMapView', element: 'root'});
    this._handleRemoveInputBubble = this._handleRemoveInputBubble.bind(this);
  }

  createSliceItemSource(args: {
    mapstate: ProteinLinearMapState;
    tabstate: ProteinMapTabState;
  }): ProteinSliceItemSource {
    return new ProteinSliceItemSource(args);
  }

  addPolymersForSearch(): void {
    ProteinActions.addProteinsForSearch([this.model.protein().id]);
  }

  removePolymersForSearch(): void {
    ProteinActions.removeProteinsFromSearch([this.model.protein().id]);
  }

  storeRegionInBenchlingClipboard(e: JQueryEventObject): string {
    const protein = this.mapstate.protein();
    const selection = protein.selection().assertNotEmpty();
    const aminoAcids = protein.residueSlice(selection.start(), selection.end());
    const clipboardDesc = bclip.clipboard.stdDesc(selection);
    bclip.clipboard.store(aminoAcids);
    return bclip.clipboard.getHash(aminoAcids, clipboardDesc);
  }

  getType(): Type {
    return this.mapstate.get('layout').AminoAcids.get('type');
  }

  handlePasteText(text: string): void {
    if (!this.model.protein().allowResidueEdits()) {
      return;
    }

    const aminoAcids = validateAndStripAminoAcids(text);
    if (aminoAcids == null) {
      return;
    }

    const protein = this.model.protein();
    const start = protein.selection().start();
    const end = protein.selection().end();
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (end !== null) {
      ProteinActions.replaceAminoAcids(protein.id, {start, end, aminoAcids});
    } else {
      ProteinActions.insertAminoAcids(protein.id, {position: start, aminoAcids});
    }
    selectRange(protein.id, start, start + aminoAcids.length);
  }

  override undo(): void {
    const protein = this.model.protein();
    if (!protein.allowResidueEdits()) {
      return;
    }
    ProteinActions.undo(protein.id);
  }

  handleCtrlShiftC(e: JQueryEventObject): void {
    // Proteins don't have a special copy menu. We prevent default behavior so the
    // element-select browser behavior in chrome doesn't trigger.
    e.preventDefault();
  }

  override _getBubbleOffsetClass(): string {
    return '.aminoAcids';
  }

  override repositionBubble(): void {
    if ($('.input-bubble').length > 0) {
      this.showInputBubble({operation: this.currentOperation});
    }
  }

  // TODO: Consolidate with SequenceMapUtil.showInputBubble
  showInputBubble(options?: ShowInputBubbleOptions): void {
    const pos = this._calculateBubblePos();
    if (pos == null) {
      this._handleRemoveInputBubble();
      return;
    }

    const {top, left, offset, isPositionedAbove} = pos;
    const protein = this.mapstate.protein();
    const selection = protein.selection();
    this.currentOperation =
      options?.operation ??
      (selection.isNotEmpty() ? INPUT_BUBBLE_OPERATION.REPLACE : INPUT_BUBBLE_OPERATION.INSERT);

    renderToDOM(
      <ProteinInputBubble
        proteinId={protein.id}
        selection={selection}
        top={top}
        left={left}
        offset={offset}
        isPositionedAbove={isPositionedAbove}
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onRequestClose={this._handleRemoveInputBubble}
        pasteText={options?.pasteText ?? ''}
        operation={this.currentOperation}
      />,
      this.$reactMount[0]
    );
    if (
      options?.pasteText == null &&
      ![INPUT_BUBBLE_OPERATION.DELETE, INPUT_BUBBLE_OPERATION.CUT].includes(this.currentOperation)
    ) {
      $('.input-bubble').find('input').focus();
    } else {
      $('.input-bubble').focus();
    }
  }

  _handleRemoveInputBubble(): void {
    ReactDOM.unmountComponentAtNode(this.$reactMount[0]);
    // this.inputBubbleView is always null on the protein map since we use the React input bubble instead
    this.inputBubbleView = null;
  }

  isValidCharacter(char: string): boolean {
    return ProteinProperties.areAminoAcidsValid(char);
  }
}
ProteinMap.initStatics();

class ProteinMapOptionsView extends Backbone.View<ProteinMapTabState> {
  mapstate!: ProteinLinearMapState;

  override initialize(): void {
    this.mapstate = this.model.get('mapstate');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:zoom', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('polymer'), 'change:banner', this.render);
    ProteinStore.addChangeListener(() => this.render());
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('visibilityOptions'), 'change', this.render);
    if (BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS) {
      listenToAntibodyVisibilitySettings(
        this.mapstate.get('visibilityOptions'),
        this,
        this.render.bind(this)
      );
    }
  }

  override render(): this {
    const protein = this.mapstate.get('polymer');
    renderToDOM(
      <ProteinMapOptions
        aminoAcids={protein.aminoAcids()}
        onSearch={() => {
          ProteinActions.openSearchView();
        }}
        zoomSliderProps={{
          min: this.mapstate.get('minZoom'),
          max: this.mapstate.get('maxZoom'),
          value: this.mapstate.get('zoom'),
          step: this.mapstate.get('stepZoom'),
          disabled: false,
          onChange: (zoom) => this.mapstate.setZoom(zoom),
          zoomBigger: () => this.mapstate.changeZoomByStep(10),
          zoomSmaller: () => this.mapstate.changeZoomByStep(-10),
        }}
        visibilityOptions={this.mapstate.get('visibilityOptions')}
        featureCounts={{
          annotations: protein.annotations().length,
          parts: protein.parts().length,
        }}
        proteinId={protein.id}
        isLatest={protein.isLatest()}
        allowResidueEdits={protein.allowResidueEdits()}
        isArchived={protein.isArchived()}
        isContainedInComplexPolymerData={protein.isContainedInComplexPolymerData()}
        allowFeatureEdits={protein.allowFeatureEdits() ?? false}
        permissions={protein.permissions()}
      />,
      this.el
    );
    return this;
  }
}

class SimplifiedProteinMapOptionsView extends Backbone.View<ProteinMapTabState> {
  mapstate!: ProteinLinearMapState;

  override initialize(): void {
    this.mapstate = this.model.get('mapstate');
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate, 'change:zoom', this.render);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('polymer'), 'change:banner', this.render);
    ProteinStore.addChangeListener(() => this.render());
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.mapstate.get('visibilityOptions'), 'change', this.render);
  }

  override render(): this {
    renderToDOM(
      <SimplifiedProteinMapOptions
        zoomSliderProps={{
          min: this.mapstate.get('minZoom'),
          max: this.mapstate.get('maxZoom'),
          value: this.mapstate.get('zoom'),
          step: this.mapstate.get('stepZoom'),
          disabled: false,
          onChange: (zoom) => this.mapstate.setZoom(zoom),
          zoomBigger: () => this.mapstate.changeZoomByStep(10),
          zoomSmaller: () => this.mapstate.changeZoomByStep(-10),
        }}
        visibilityOptions={this.mapstate.get('visibilityOptions')}
        featureCounts={{annotations: this.mapstate.get('polymer').annotations().length}}
        aminoAcids={this.mapstate.get('polymer').aminoAcids()}
      />,
      this.el
    );
    return this;
  }
}

export {
  ProteinMap as TabView,
  ProteinMapOptionsView as OptionsView,
  SimplifiedProteinMapOptionsView as SimplifiedOptionsView,
};
